/**
 * Marketing Demand Quality Page
 * Extracted from App.js - Phase 7E Frontend Refactor
 * Shows marketing sources ranked by demand quality
 */
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

const MarketingDemandPage = () => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [sources, setSources] = useState([]);
  const [totalVerifiedHigh, setTotalVerifiedHigh] = useState(0);
  const [totalSources, setTotalSources] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiFetch("/demand/scores/marketing", { method: "GET" });
        setSources(res.data.sources || []);
        setTotalVerifiedHigh(res.data.total_verified_high_demand || 0);
        setTotalSources((res.data.sources || []).length);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
        toast.error("Failed to load marketing data");
      }
    };
    fetchData();
  }, [apiFetch, toast]);

  const getDemandLevelColor = (level) => {
    switch (level) {
      case "high": return "#10b981";
      case "strong": return "#3b82f6";
      case "emerging": return "#f59e0b";
      case "low": return "#6b7280";
      default: return "#6b7280";
    }
  };

  const getDemandLevelBg = (level) => {
    switch (level) {
      case "high": return "#dcfce7";
      case "strong": return "#dbeafe";
      case "emerging": return "#fef3c7";
      case "low": return "#f3f4f6";
      default: return "#f3f4f6";
    }
  };

  const formatSourceName = (source) => {
    if (source === "(direct)") return "Direct Traffic";
    if (source === "(none)") return "Unknown";
    return source.charAt(0).toUpperCase() + source.slice(1);
  };

  if (isLoading) {
    return <div className="page-container"><div className="loading-spinner">Loading marketing quality...</div></div>;
  }

  return (
    <div className="page-container marketing-quality-page">
      <div className="page-header">
        <h2>Marketing Quality</h2>
        <p className="page-subtitle">Which marketing sources are producing real buyer demand — not just clicks?</p>
      </div>

      {/* Summary Bar */}
      <div className="marketing-toolbar">
        <div className="marketing-summary-stats">
          <div className="summary-item">
            <span className="summary-number">{totalSources}</span>
            <span className="summary-text">traffic sources tracked</span>
          </div>
          {totalVerifiedHigh > 0 && (
            <div className="summary-item highlight">
              <span className="verified-high-icon">✓</span>
              <span className="summary-number">{totalVerifiedHigh}</span>
              <span className="summary-text">verified high demand</span>
            </div>
          )}
        </div>
      </div>

      {sources.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-icon">📣</div>
          <h3>Marketing data coming soon</h3>
          <p>Traffic source demand will appear here once visitors arrive with UTM parameters or from identifiable referrers.</p>
        </div>
      ) : (
        <div className="marketing-source-list">
          {sources.map((source, idx) => (
            <div key={idx} className="marketing-source-card">
              <div className="source-card-header">
                <div className="source-rank-badge">#{idx + 1}</div>
                <div className="source-main-info">
                  <div className="source-title-row">
                    <span className="source-name">{formatSourceName(source.source)}</span>
                    <div className="source-badges">
                      {source.confidence_label === "verified" && (
                        <span className="source-verified-badge">
                          <span className="vb-icon">✓</span> Verified
                        </span>
                      )}
                      <span 
                        className="source-demand-pill"
                        style={{ 
                          backgroundColor: getDemandLevelBg(source.demand_level),
                          color: getDemandLevelColor(source.demand_level)
                        }}
                      >
                        {source.demand_label}
                      </span>
                    </div>
                  </div>
                  <p className="source-explanation">{source.explanation}</p>
                </div>
              </div>

              {/* Demand Level Distribution Mini-Bar */}
              <div className="source-demand-distribution">
                <div className="distribution-label">Demand Distribution</div>
                <div className="distribution-mini-bars">
                  {["high", "strong", "emerging", "low"].map(level => {
                    const count = source.demand_level_distribution?.[level] || 0;
                    const total = source.unique_visitors || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={level} className="mini-bar-item">
                        <div className="mini-bar-track">
                          <div 
                            className="mini-bar-fill"
                            style={{ 
                              width: `${Math.max(pct, count > 0 ? 5 : 0)}%`,
                              backgroundColor: getDemandLevelColor(level)
                            }}
                          />
                        </div>
                        <span className="mini-bar-label">{level.charAt(0).toUpperCase()}: {count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Key Metrics */}
              <div className="source-key-metrics">
                <div className="key-metric">
                  <span className="km-value">{source.unlock_attempts}</span>
                  <span className="km-label">Unlock Attempts</span>
                </div>
                {source.verified_high_demand_count > 0 && (
                  <div className="key-metric verified-metric">
                    <span className="km-value">{source.verified_high_demand_count}</span>
                    <span className="km-label">Verified Demand</span>
                  </div>
                )}
                <div className="key-metric">
                  <span className="km-value">{source.unique_visitors}</span>
                  <span className="km-label">Visitors</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sources.length > 0 && (
        <div className="marketing-footer-hint">
          Sources are ranked by demand quality, not traffic volume or lead count.
        </div>
      )}
    </div>
  );
};

export default MarketingDemandPage;
